import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Autocomplete,
  Chip,
  Button,
  CircularProgress,
  FormControlLabel,
  Checkbox,
  MenuItem,
  IconButton,
  Paper,
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';
import ErrorBoundary from 'components/ErrorBoundary';


const CATEGORIES = ['initial', 'recurrent', 'upgrade', 'special', 'other'];

const ALERT_BEFORE_TYPES = [
  { value: 'day',   label: 'Day(s)' },
  { value: 'week',  label: 'Week(s)' },
  { value: 'month', label: 'Month(s)' },
  { value: 'year',  label: 'Year(s)' },
];

const VALIDITY_TYPES = [
  { index: 1, label: 'Days' },
  { index: 2, label: 'Months ending at end' },
  { index: 3, label: 'Months ending on day' },
  { index: 4, label: 'Never Expires' },
  { index: 5, label: 'Months ending at end + 1 day' },
  { index: 6, label: 'Calendar Year' },
  { index: 7, label: 'Months ending on day -1 day' },
];

const emptyRegRef = () => ({ authority: '', regulation: '', article: '' });

const schema = yup.object().shape({
  subjectCode: yup.string().required('Required'),
  subjectName: yup.string().required('Required'),
  validityType: yup.number().required('Required'),
  validityPeriod: yup.number().nullable().when('validityType', {
    is: 4,
    then: (s) => s.nullable().optional(),
    otherwise: (s) => s.min(1, 'Min 1').required('Required'),
  }),
  alertBefore: yup.number().min(0).required('Required'),
  alertBeforeType: yup.string().oneOf(['day', 'week', 'month', 'year']).required('Required'),
  passingScore: yup.number().min(0).max(100).nullable(),
  distinctionScore: yup.number().min(0).max(100).nullable(),
  newHireGracePeriodDays: yup.number().min(0).required('Required'),
});

const TrainingSubjectPage = () => {
  useAuthCheck();
  const { trainingSubjectId } = useParams();
  const navigate = useNavigate();

  const crewRanksData = useSelector((state) => state.crewRanks);
  const rankOptions = (crewRanksData || []).map((r) => r.rankCode).filter(Boolean);

  const [acTypeOptions, setAcTypeOptions] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formValues, setFormValues] = useState({
    subjectCode: '',
    subjectName: '',
    description: '',
    category: 'recurrent',
    validityType: 3,
    validityPeriod: 12,
    alertBefore: 2,
    alertBeforeType: 'month',
    newHireGracePeriodDays: 0,
    passingScore: null,
    distinctionScore: null,
    applicableRanks: [],
    applicableAcTypes: [],
    regulatoryRef: [],
    isActive: true,
  });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          api.get('/actypes/active')
            .then((res) => setAcTypeOptions((res.data || []).map((a) => a.acTypeCode).filter(Boolean)))
            .catch(() => {}),
          trainingSubjectId && api.get(`/trainingsubjects/${trainingSubjectId}`)
            .then(({ data: d }) => setFormValues({
              subjectCode: d.subjectCode || '',
              subjectName: d.subjectName || '',
              description: d.description || '',
              category: d.category || 'recurrent',
              validityType: d.validityType ?? 2,
              validityPeriod: d.validityPeriod ?? 12,
              alertBefore: d.alertBefore ?? 2,
              alertBeforeType: d.alertBeforeType || 'month',
              passingScore: d.passingScore ?? '',
              distinctionScore: d.distinctionScore ?? '',
              newHireGracePeriodDays: d.newHireGracePeriodDays ?? 0,
              applicableRanks: d.applicableRanks || [],
              applicableAcTypes: d.applicableAcTypes || [],
              regulatoryRef: d.regulatoryRef?.length
                ? d.regulatoryRef.map((r) => ({
                    authority: r.authority || '',
                    regulation: r.regulation || '',
                    article: r.article || '',
                  }))
                : [],
              isActive: d.isActive ?? true,
            })),
        ]);
      } catch (err) {
        setError(err);
        toast.error(err?.response?.data?.message || 'Failed to load subject!');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [trainingSubjectId]);

  const handleFormSubmit = async (values) => {
    try {
      setIsSubmitting(true);
      const payload = {
        ...values,
        passingScore: values.passingScore === '' ? null : Number(values.passingScore),
        distinctionScore: values.distinctionScore === '' ? null : Number(values.distinctionScore),
      };
      const method = trainingSubjectId ? 'put' : 'post';
      const url = trainingSubjectId
        ? `/trainingsubjects/update/${trainingSubjectId}`
        : '/trainingsubjects/new';
      await api({ method, url, data: payload });
      toast.success(trainingSubjectId ? 'Updated successfully.' : 'Created successfully.');
      navigate('/trainingsubjects');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to save!');
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <ErrorBoundary>
      <Box className="main-container-box">
        <FlexBetween>
          <Header
            title="Training Subject"
            subtitle={loading ? 'Loading...' : trainingSubjectId ? 'Update' : 'Create'}
          />
          {!loading && (
            <StyledButton onClick={() => navigate('/trainingsubjects')}>
              <ArrowBackIosNewIcon />
            </StyledButton>
          )}
        </FlexBetween>

        {loading ? (
          <FlexCenter height={LAYOUT_HEIGHT}><CircularProgress /></FlexCenter>
        ) : error ? (
          <FlexCenter height={LAYOUT_HEIGHT}>
            <Typography color="error">Error: {error?.message}</Typography>
          </FlexCenter>
        ) : (
          <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
            <Formik
              initialValues={formValues}
              enableReinitialize
              validationSchema={schema}
              onSubmit={handleFormSubmit}
            >
              {({ values, errors, touched, handleBlur, handleChange, handleSubmit, setFieldValue }) => (
                <form onSubmit={handleSubmit}>
                  <Box
                    display="grid"
                    gap={2}
                    gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                  >
                    {/* Row 1: identifiers */}
                    <TextField
                      fullWidth
                      required
                      variant="filled"
                      label="Subject Code"
                      name="subjectCode"
                      value={values.subjectCode}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.subjectCode && !!errors.subjectCode}
                      helperText={touched.subjectCode && errors.subjectCode}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 4' } }}
                    />
                    <TextField
                      fullWidth
                      required
                      variant="filled"
                      label="Subject Name"
                      name="subjectName"
                      value={values.subjectName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.subjectName && !!errors.subjectName}
                      helperText={touched.subjectName && errors.subjectName}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 8' } }}
                    />

                    {/* Row 2 */}
                    <TextField
                      select
                      fullWidth
                      variant="filled"
                      label="Category"
                      name="category"
                      value={values.category}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 4' } }}
                    >
                      {CATEGORIES.map((c) => (
                        <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      fullWidth
                      variant="filled"
                      multiline
                      rows={1}
                      label="Description"
                      name="description"
                      value={values.description}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 8' } }}
                    />

                    {/* Row 2b: applicable ranks + ac types */}
                    <Autocomplete
                      multiple
                      options={rankOptions}
                      value={values.applicableRanks}
                      onChange={(_, v) => setFieldValue('applicableRanks', v)}
                      renderTags={(selected, getTagProps) =>
                        selected.map((opt, idx) => (
                          <Chip key={opt} label={opt} size="small" {...getTagProps({ index: idx })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="filled"
                          label="Applicable Ranks"
                          placeholder={values.applicableRanks.length === 0 ? 'All ranks' : ''}
                        />
                      )}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 6' } }}
                    />

                    <Autocomplete
                      multiple
                      options={acTypeOptions}
                      value={values.applicableAcTypes}
                      onChange={(_, v) => setFieldValue('applicableAcTypes', v)}
                      renderTags={(selected, getTagProps) =>
                        selected.map((opt, idx) => (
                          <Chip key={opt} label={opt} size="small" {...getTagProps({ index: idx })} />
                        ))
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="filled"
                          label="Applicable AC Types"
                          placeholder={values.applicableAcTypes.length === 0 ? 'All types' : ''}
                        />
                      )}
                      sx={{ gridColumn: { xs: 'span 12', lg: 'span 6' } }}
                    />

                    {/* Row 3: validity + alert */}
                    <TextField
                      select
                      fullWidth
                      required
                      variant="filled"
                      label="Validity Type"
                      name="validityType"
                      value={values.validityType}
                      onChange={(e) => {
                        handleChange(e);
                        if (Number(e.target.value) === 4) setFieldValue('validityPeriod', null);
                      }}
                      onBlur={handleBlur}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 4' } }}
                    >
                      {VALIDITY_TYPES.map((t) => (
                        <MenuItem key={t.index} value={t.index}>
                          {t.index}. {t.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    {values.validityType !== 4 && (
                      <TextField
                        fullWidth
                        required
                        variant="filled"
                        type="number"
                        label={values.validityType === 1 ? 'Validity (days)' : 'Validity (months)'}
                        name="validityPeriod"
                        value={values.validityPeriod ?? ''}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={!!touched.validityPeriod && !!errors.validityPeriod}
                        helperText={touched.validityPeriod && errors.validityPeriod}
                        slotProps={{ inputProps: { min: 1 } }}
                        sx={{ gridColumn: { xs: 'span 12', sm: 'span 3', lg: 'span 2' } }}
                      />
                    )}

                    <TextField
                      fullWidth
                      required
                      variant="filled"
                      type="number"
                      label="Alert Before"
                      name="alertBefore"
                      value={values.alertBefore}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.alertBefore && !!errors.alertBefore}
                      helperText={touched.alertBefore && errors.alertBefore}
                      slotProps={{ inputProps: { min: 0 } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 3', lg: 'span 2' } }}
                    />
                    <TextField
                      select
                      fullWidth
                      required
                      variant="filled"
                      label="Alert Unit"
                      name="alertBeforeType"
                      value={values.alertBeforeType}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 6', lg: 'span 4' } }}
                    >
                      {ALERT_BEFORE_TYPES.map((t) => (
                        <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                      ))}
                    </TextField>

                    {/* Row 4: scores + grace */}
                    <TextField
                      fullWidth
                      variant="filled"
                      type="number"
                      label="New Hire Grace Period (days)"
                      name="newHireGracePeriodDays"
                      value={values.newHireGracePeriodDays}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.newHireGracePeriodDays && !!errors.newHireGracePeriodDays}
                      helperText={touched.newHireGracePeriodDays && errors.newHireGracePeriodDays}
                      slotProps={{ inputProps: { min: 0 } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 4', lg: 'span 4' } }}
                    />
                    <TextField
                      fullWidth
                      variant="filled"
                      type="number"
                      label="Passing Score (0–100)"
                      name="passingScore"
                      value={values.passingScore}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.passingScore && !!errors.passingScore}
                      helperText={touched.passingScore && errors.passingScore}
                      slotProps={{ inputProps: { min: 0, max: 100 } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 4', lg: 'span 4' } }}
                    />
                    <TextField
                      fullWidth
                      variant="filled"
                      type="number"
                      label="Distinction Score (0–100)"
                      name="distinctionScore"
                      value={values.distinctionScore}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={!!touched.distinctionScore && !!errors.distinctionScore}
                      helperText={touched.distinctionScore && errors.distinctionScore}
                      slotProps={{ inputProps: { min: 0, max: 100 } }}
                      sx={{ gridColumn: { xs: 'span 12', sm: 'span 4', lg: 'span 4' } }}
                    />

                    {/* Row 5: isActive */}
                    <Box sx={{ gridColumn: 'span 12', display: 'flex', alignItems: 'center' }}>
                      <FormControlLabel
                        label="Is Active"
                        control={
                          <Checkbox checked={values.isActive} onChange={handleChange} name="isActive" color="primary" />
                        }
                      />
                    </Box>

                    {/* Row 6: Regulatory References */}
                    <Box sx={{ gridColumn: 'span 12' }}>
                      <FlexBetween mb={1}>
                        <Typography variant="subtitle2" fontWeight="bold">
                          Regulatory References
                        </Typography>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => setFieldValue('regulatoryRef', [...values.regulatoryRef, emptyRegRef()])}
                        >
                          <AddCircleOutlineIcon />
                        </IconButton>
                      </FlexBetween>

                      {values.regulatoryRef.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No regulatory references. Click + to add.
                        </Typography>
                      ) : (
                        values.regulatoryRef.map((ref, idx) => (
                          <Paper key={idx} elevation={1} sx={{ p: 1.5, mb: 1, position: 'relative', pr: 5 }}>
                            <IconButton
                              size="small" color="error"
                              onClick={() => setFieldValue('regulatoryRef', values.regulatoryRef.filter((_, i) => i !== idx))}
                              sx={{ position: 'absolute', top: 6, right: 6 }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                              Ref #{idx + 1}
                            </Typography>
                            <Box display="grid" gap={1.5} gridTemplateColumns="repeat(12, minmax(0, 1fr))">
                              <TextField
                                size="small" variant="filled" label="Authority"
                                placeholder="e.g. ICAO, CAAV"
                                value={ref.authority}
                                onChange={(e) => {
                                  const updated = [...values.regulatoryRef];
                                  updated[idx] = { ...updated[idx], authority: e.target.value };
                                  setFieldValue('regulatoryRef', updated);
                                }}
                                sx={{ gridColumn: { xs: 'span 12', lg: 'span 3' } }}
                              />
                              <TextField
                                size="small"
                                variant="filled"
                                label="Regulation"
                                placeholder="e.g. Annex 1, Part 66"
                                value={ref.regulation}
                                onChange={(e) => {
                                  const updated = [...values.regulatoryRef];
                                  updated[idx] = { ...updated[idx], regulation: e.target.value };
                                  setFieldValue('regulatoryRef', updated);
                                }}
                                sx={{ gridColumn: { xs: 'span 12', lg: 'span 5' } }}
                              />
                              <TextField
                                size="small"
                                variant="filled"
                                label="Article"
                                placeholder="e.g. 3.2.1"
                                value={ref.article}
                                onChange={(e) => {
                                  const updated = [...values.regulatoryRef];
                                  updated[idx] = { ...updated[idx], article: e.target.value };
                                  setFieldValue('regulatoryRef', updated);
                                }}
                                sx={{ gridColumn: { xs: 'span 12', lg: 'span 4' } }}
                              />
                            </Box>
                          </Paper>
                        ))
                      )}
                    </Box>

                    <Button
                      fullWidth 
                      type="submit" 
                      variant="contained" 
                      color="secondary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{ gridColumn: 'span 12', height: 42 }}
                    >
                      {isSubmitting ? 'Saving…' : trainingSubjectId ? 'Update' : 'Create'}
                    </Button>
                  </Box>
                </form>
              )}
            </Formik>
          </Box>
        )}
      </Box>
    </ErrorBoundary>
  );
};

export default TrainingSubjectPage;
